import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import Papa from "papaparse";

const filterFieldSx = { minWidth: "220px", "& .MuiOutlinedInput-root": { height: "38px" } };
const filterSelectSx = {
  minWidth: "220px",
  "& .MuiOutlinedInput-root": { height: "38px" },
  "& .MuiSelect-select": { fontSize: "0.8rem" },
};
const filterLabelProps = { shrink: true, style: { fontSize: "0.8rem" } };
const filterInputProps = { style: { fontSize: "0.8rem" } };

const STATUS_STYLES = {
  ACTIVE:    { bg: "#d4edda", color: "#155724" },
  COMPLETED: { bg: "#cce5ff", color: "#004085" },
  EXPIRED:   { bg: "#f8d7da", color: "#721c24" },
};

function StatusBadge({ value }) {
  const style = STATUS_STYLES[value] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

StatusBadge.propTypes = {
  value: PropTypes.string.isRequired,
};

function QrModal({ open, onClose, qrData, description }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        QR Code — {description}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center", pb: 3 }}>
        {qrData ? (
          <img
            src={`data:image/png;base64,${qrData}`}
            alt="QR Code"
            style={{ width: "100%", maxWidth: 260, height: "auto" }}
          />
        ) : (
          <MDTypography variant="body2" color="text">No QR data available</MDTypography>
        )}
      </DialogContent>
    </Dialog>
  );
}

QrModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  qrData: PropTypes.string,
  description: PropTypes.string,
};

QrModal.defaultProps = {
  qrData: null,
  description: "",
};

function StatusCell({ value }) {
  return <StatusBadge value={value} />;
}
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const day = d.getDate();
    const month = MONTH_NAMES[d.getMonth()];
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${day} ${month} ${year} ${hh}:${min}:${ss}`;
  }
  // Fallback: handle "DD/MM/YYYY HH:MM:SS"
  const [datePart, timePart] = String(dateStr).split(" ");
  const parts = datePart.split("/");
  if (parts.length === 3) {
    const [first, second, third] = parts;
    const parsed = new Date(`${third}-${second}-${first}`);
    if (!isNaN(parsed.getTime())) {
      const day = parsed.getDate();
      const month = MONTH_NAMES[parsed.getMonth()];
      const year = parsed.getFullYear();
      return `${day} ${month} ${year} ${timePart ?? ""}`.trim();
    }
  }
  return dateStr;
};

function PaymentLink() {
  const { predefinedRanges } = reportsLineChartData;
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const today = new Date();
  const [dateRange, setDateRange] = useState([today, today]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [qrModal, setQrModal] = useState({ open: false, qrData: null, description: "" });
  const api = useApi();
  const navigate = useNavigate();

  const filteredRows = rows.filter((row) => {
    const matchesStatus = statusFilter === "All" || row.paymentLinkStatus === statusFilter;

    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      [row.paymentLinkId, row.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));

    return matchesStatus && matchesSearch;
  });

  const formatApiDate = (date, isEnd = false) => {
    const d = new Date(date);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    const time = isEnd ? "23:59:00" : "00:00:00";
    return `${mm}/${dd}/${yyyy} ${time}`;
  };

  const columns = [
    {
      Header: "Link ID",
      accessor: "paymentLinkId",
      width: "6%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          title={value}
          sx={{
            display: "block",
            maxWidth: 90,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Copy Link",
      accessor: "paymentLinkId_copy",
      width: "7%",
      align: "center",
      /* eslint-disable react/prop-types */
      Cell: ({ row }) => {
        const pgBase = (process.env.REACT_APP_PAYMENT_GATEWAY_URL || "").replace(/\/$/, "");
        const url = `${pgBase}/api/v1/payments-link/${row.original.paymentLinkId}`;
        return (
          <IconButton
            size="small"
            title={url}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(url);
              toast.success("Payment link copied!");
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        );
      },
      /* eslint-enable react/prop-types */
    },
    {
      Header: "QR",
      accessor: "hasQr",
      width: "7%",
      align: "center",
      /* eslint-disable react/prop-types */
      Cell: ({ row }) =>
        row.original.hasQr && row.original.qrCodeData ? (
          <img
            src={`data:image/png;base64,${row.original.qrCodeData}`}
            alt="QR"
            style={{ width: 48, height: 48, cursor: "pointer", display: "block" }}
            onClick={(e) => {
              e.stopPropagation();
              setQrModal({
                open: true,
                qrData: row.original.qrCodeData,
                description: row.original.description,
              });
            }}
          />
        ) : (
          <MDTypography variant="caption" color="text">—</MDTypography>
        ),
      /* eslint-enable react/prop-types */
    },
    {
      Header: "Transactions",
      accessor: "paymentLinkId_transactions",
      width: "7%",
      align: "center",
      /* eslint-disable react/prop-types */
      Cell: ({ row }) => (
        <IconButton
          size="small"
          title="View transactions"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/payment-links/${row.original.paymentLinkId}`);
          }}
        >
          <ReceiptLongIcon fontSize="small" />
        </IconButton>
      ),
      /* eslint-enable react/prop-types */
    },
    {
      Header: "Amount (Currency)",
      accessor: (row) => `${row.amount} (${row.currency ?? "—"})`,
      width: "10%",
      align: "left",
    },
    {
      Header: "Status",
      accessor: "paymentLinkStatus",
      width: "8%",
      align: "left",
      Cell: StatusCell,
    },
    {
      Header: "Start Date",
      accessor: "validFromDate",
      width: "11%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <span>{formatDisplayDate(value)}</span>,
    },
    {
      Header: "End Date",
      accessor: "validTillDate",
      width: "11%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <span>{value ? formatDisplayDate(value) : "—"}</span>,
    },
    { Header: "Description", accessor: "description", width: "12%", align: "left" },
    { Header: "Quantity", accessor: "quantity", width: "6%", align: "left" },
    {
      Header: "Remaining",
      accessor: (row) => {
        const qty = parseInt(row.quantity, 10);
        const used = parseInt(row.totalPayments, 10) || 0;
        return isNaN(qty) ? "—" : Math.max(0, qty - used);
      },
      width: "7%",
      align: "left",
    },
    { Header: "QR Payments", accessor: "qrPayments", width: "7%", align: "left" },
    { Header: "Direct Payments", accessor: "directLinkPayments", width: "8%", align: "left" },
    { Header: "Total Payments", accessor: "totalPayments", width: "8%", align: "left" },
  ];

  const fetchData = (range) => {
    if (!range || range.length !== 2) return;
    const body = {
      startDate: formatApiDate(range[0], false),
      endDate: formatApiDate(range[1], true),
      status: null,
    };
    api.post("payments-link/paymentLinkDetails", body)
      .then((response) => {
        const result = response.data?.data;
        setRows(Array.isArray(result) ? result : []);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRows([]);
      });
  };

  const handleDownloadCSV = () => {
    if (rows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const csvColumns = columns.filter((c) => c.Header !== "QR" && c.Header !== "Transactions" && c.Header !== "Copy Link");
    const headers = csvColumns.map((c) => c.Header);
    const values = rows.map((row) =>
      csvColumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Payment_Links_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    fetchData(dateRange);
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  Payment Link
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  {rows.length} payment link{rows.length === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>
              <DateRangePicker
                disabledDate={afterToday()}
                ranges={predefinedRanges}
                onChange={(range) => {
                  if (range && range.length === 2) {
                    setDateRange(range);
                    fetchData(range);
                  }
                }}
                format="dd MMMM yyyy"
                value={[new Date(dateRange[0]), new Date(dateRange[1])]}
              />
              <MDBox flex={1} display="flex" justifyContent="flex-end" gap={1}>
                <MDButton variant="outlined" color="secondary" size="small" onClick={handleDownloadCSV}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">download</Icon>
                  Export
                </MDButton>
                <MDButton variant="gradient" color={sidenavColor} size="small" onClick={() => navigate("/payment-link/new")}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                  New payment link
                </MDButton>
              </MDBox>
            </MDBox>

            <Card
              sx={({ boxShadows }) => ({
                border: "1px solid",
                borderColor: "grey.300",
                boxShadow: boxShadows.sm,
                borderRadius: "12px",
                overflow: "hidden",
              })}
            >
              <MDBox
                display="flex"
                alignItems="center"
                gap={2}
                px={2.25}
                py={1.25}
                bgColor="grey-100"
                sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
              >
                <TextField
                  label="Search"
                  placeholder="Link ID or description"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={filterFieldSx}
                />
                <TextField
                  select
                  label="Status"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                </TextField>
              </MDBox>
              <DataTable
                table={{ columns, rows: filteredRows }}
                showTotalEntries
                isSorted
                noEndBorder
                hideExpandColumn
                entriesPerPage
                entriesPerPagePosition="bottom"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <QrModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrData: null, description: "" })}
        qrData={qrModal.qrData}
        description={qrModal.description}
      />
    </DashboardLayout>
  );
}

export default PaymentLink;
